import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ElementRef, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { GridApiService } from './services/grid-api.service';
import { GridEditStore } from './services/grid-edit-store.service';
import { GridColumnDef, GridDefinition, SortDef, FilterGroup, BulkOp, BulkOpType, GridPage, BulkOpsRequest } from './grid-types';
import { clearView, loadView, saveView } from './wc-grid-view-store';
import { LoginService } from '../../../services/login-service';

type EditTransport = 'server'|'memory';

@Component({
  selector: 'wc-grid',
  standalone: false,
  styleUrls: ['./grid-theme.css'],
  template: `
  <div *ngIf="menuOpen"
     class="wcg-menu"
     [style.left.px]="menuX"
     [style.top.px]="menuY"
     (click)="$event.stopPropagation()">
  <button *ngFor="let item of contextMenuItems"
          class="wcg-menu__item"
          [disabled]="item.disabled?.(menuRow!)"
          (click)="runMenu(item.id)">
    {{item.label}}
  </button>
</div>

  <div *ngIf="def" class="wcg">

  <!-- Delete confirm modal -->
<div *ngIf="deleteOpen" class="wcg-modal">
  <div class="wcg-modal__panel">
    <div class="wcg-modal__header">
      <strong>Delete {{def.title}}</strong>
      <span class="wcg-modal__spacer"></span>
      <button class="wcg-btn wcg-btn--ghost" (click)="closeDelete()">Close</button>
    </div>

    <div class="wcg__muted wcg-modal__hint">
      This will permanently delete the record{{def.softDelete ? ' (soft delete)' : ''}}.
      Are you sure?
    </div>

    <div class="wcg-modal__footer">
      <button class="wcg-btn wcg-btn--danger"
              (click)="confirmDelete()"
              [disabled]="deleting">
        Yes, delete
      </button>
      <button class="wcg-btn wcg-btn--ghost"
              (click)="closeDelete()"
              [disabled]="deleting">
        Cancel
      </button>
    </div>
  </div>
</div>

  <!-- Soft-check confirm modal: advisory warnings from the server's pre-check, never blocking on their own -->
<div *ngIf="warnOpen" class="wcg-modal">
  <div class="wcg-modal__panel">
    <div class="wcg-modal__header">
      <strong>Please confirm</strong>
    </div>

    <div class="wcg-modal__hint">
      <p *ngFor="let w of warnings">{{w}}</p>
      <p class="wcg__muted">Do you want to continue anyway?</p>
    </div>

    <div class="wcg-modal__footer">
      <button class="wcg-btn wcg-btn--primary" (click)="proceedPastWarnings()">Add anyway</button>
      <button class="wcg-btn wcg-btn--ghost" (click)="declineWarnings()">Go back</button>
    </div>
  </div>
</div>

  <div class="wcg__toolbar" *ngIf="def?.showHeader">
    <div class="wcg__title">
      <strong>{{def.titlePlural}}</strong>
      <span class="wcg__badge" *ngIf="editTransport==='memory'">Memory mode</span>
    </div>

    <div class="wcg__toolbar-spacer"></div>

    <div class="wcg__actions">
      <button class="wcg-btn wcg-btn--ghost" (click)="toggleColumns()">Columns</button>
      <button class="wcg-btn wcg-btn--ghost" (click)="resetView()">Reset view</button>

      <button class="wcg-btn wcg-btn--primary" *ngIf="def.canCreate" (click)="openCreate()">+ Add</button>

      <ng-container *ngIf="editTransport==='server'">
        <button class="wcg-btn wcg-btn--success"
                (click)="saveToServer()"
                [disabled]="saving || dirtyCount===0">
          Save <span class="wcg__pill">({{dirtyCount}})</span>
        </button>

        <button class="wcg-btn wcg-btn--danger"
                (click)="discard()"
                [disabled]="saving || dirtyCount===0">
          Discard
        </button>
      </ng-container>
    </div>
  </div>

  <!-- Floating add button — shown when toolbar is hidden but grid allows create -->
  <button class="wcg__fab"
          *ngIf="!def?.showHeader && def?.canCreate"
          (click)="openCreate()"
          title="Add record">
    +
  </button>

  <div class="wcg__search" *ngIf="showQuickSearch">
    <span class="wcg__search-icon" aria-hidden="true">🔍</span>
    <input class="wcg__input wcg__input--search"
           type="text"
           [placeholder]="searchPlaceholder"
           [ngModel]="quickSearch"
           (ngModelChange)="onQuickSearchInput($event)" />
    <button class="wcg-btn wcg-btn--ghost wcg-btn--xs"
            *ngIf="quickSearch"
            (click)="clearQuickSearch()">
      Clear
    </button>
  </div>

  <div class="wcg__filter" *ngIf="def?.allowFilters">
    <wc-filter-builder
      [columns]="filterableColumns"
      [gridKey]="dashboardId ? '' : def.gridKey"
      [dashboardId]="dashboardId ?? ''"
      [(model)]="filter"
      (apply)="onFilterChanged()">
    </wc-filter-builder>
  </div>

  <div class="wcg__table-wrap" #tableWrap>
    <table class="wcg__table">
      <thead class="wcg__throw">
        <tr>
          <th class="wcg__th wcg__th--icon" *ngIf="def.canDelete">Del</th>
          <th class="wcg__th wcg__th--icon" *ngIf="def.editMode==='popup'">Edit</th>

          <th *ngFor="let c of visibleColumnDefs"
              class="wcg__th"
              (click)="toggleSort(c)"
              [class.wcg__th--sortable]="c.sortable"
              [style.width.px]="colWidth(c.field)"
              [style.minWidth.px]="colWidth(c.field)"
              [class.wcg__th--sorted]="sortDir(c.field)">
              <span class="wcg__col-resizer"
      (mousedown)="startResize($event, c.field)"
        (dblclick)="autoSizeColumn($event, c.field)"
      (click)="$event.stopPropagation()"></span>
            <span class="wcg__th-text">{{c.header}}</span>
            <span class="wcg__sort" *ngIf="sortDir(c.field) as d">
              {{d==='asc'?'▲':'▼'}}
            </span>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr *ngFor="let r of displayRows; let i = index"
            class="wcg__tr"
            [class.wcg__tr--alt]="i % 2 === 1"
            [class.wcg__tr--selected]="isSelected(r)"
            [class.wcg__tr--deleted]="r['__rowDeleted']"
            [class.wcg__tr--noedit]="!canRowEdit(r)"
            [class.wcg__tr--nodelete]="!canRowDelete(r)"
            [class.wcg__tr--new]="r['__isNew']"
              (click)="selectRow(r)"
              (contextmenu)="onRowContextMenu($event, r)">

          <td class="wcg__td wcg__td--icon" *ngIf="def.canDelete">
            <button class="wcg-btn wcg-btn--danger wcg-btn--xs"
                    *ngIf="!isRowDeleted(r) && canRowDelete(r)"
                    (click)="requestDelete(r)">
              Delete
            </button>

            <button class="wcg-btn wcg-btn--ghost wcg-btn--xs wcg-btn--undelete"
                    *ngIf="isRowDeleted(r)"
                    (click)="undoDelete(r)">
              Undo
            </button>
          </td>

          <td class="wcg__td wcg__td--icon" *ngIf="def.editMode==='popup'">
            <button *ngIf="canRowEdit(r)" class="wcg-btn wcg-btn--ghost wcg-btn--xs wcg-btn--edit"
                    (click)="openEdit(r)"
                    [disabled]="!canRowEdit(r) || isRowDeleted(r)">
              Edit
            </button>
          </td>

          <td *ngFor="let c of visibleColumnDefs"
              class="wcg__td"
              [style.width.px]="colWidth(c.field)"
              [style.minWidth.px]="colWidth(c.field)"
              [class.wcg__td--dirty]="isDirty(r, c.field)"
              [class.wcg__td--readonly]="isReadOnlyCell(r, c)"
              [class.wcg__td--editable]="isColumnEditable(c, r)"
              [class.wcg__td--focused]="isCellFocused(r, c)"
              [class.wcg__td--invalid]="isCellInvalid(r, c.field)"
              (click)="focusCell(r, c)"
              (focusout)="onCellFocusOut($event)">

            <ng-container [ngSwitch]="cellMode(c, r)">
              <wc-lookup-editor
                *ngSwitchCase="'edit-lookup'"
                class="wcg__control"
                [gridKey]="def.gridKey"
                [lookupKey]="c.lookupKey!"
                [filter]="c.cascadeLookup ? filter : null"
                [(value)]="r[c.field]"
                [required]="c.required"
                (valueChange)="onInlineEdit(r, c.field, $event)">
              </wc-lookup-editor>

              <input *ngSwitchCase="'edit-text'"
                     class="wcg__input"
                     [(ngModel)]="r[c.field]"
                     (ngModelChange)="onInlineEdit(r, c.field, $event)"
                     [required]="c.required" />

              <input *ngSwitchCase="'edit-number'"
                     class="wcg__input wcg__input--number"
                     type="number"
                     [(ngModel)]="r[c.field]"
                     (ngModelChange)="onInlineEdit(r, c.field, $event)"
                     [required]="c.required" />

              <input *ngSwitchCase="'edit-date'"
                     class="wcg__input wcg__input--date"
                     type="date"
                    [ngModel]="toIsoDateOnly(r[c.field])"
                    (ngModelChange)="onInlineEdit(r, c.field, fromIsoDateOnly($event))"
                     [required]="c.required" />

              <input *ngSwitchCase="'edit-datetime'"
                   class="wcg__input wcg__input--date"
                   type="datetime-local"
                   [ngModel]="toLocalDateTime(r[c.field])"
                   (ngModelChange)="onInlineEdit(r, c.field, fromLocalDateTime($event))"
                   [required]="c.required" />

              <label *ngSwitchCase="'edit-bool'" class="wcg__checkbox">
                <input type="checkbox"
                       [(ngModel)]="r[c.field]"
                       (ngModelChange)="onInlineEdit(r, c.field, $event)" />
                <span class="wcg__checkbox-ui"></span>
              </label>

              <span *ngSwitchDefault class="wcg__cell-text">
                {{ formatCell(r[c.field], c.dataType, c.format) }}
              </span>
            </ng-container>
          </td>

        </tr>
      </tbody>
    </table>
  </div>

  <div class="wcg__pager">
    <button class="wcg-btn wcg-btn--ghost" (click)="reload(1)" [disabled]="page<=1">First</button>
    <button class="wcg-btn wcg-btn--ghost" (click)="reload(page-1)" [disabled]="page<=1">Prev</button>

    <div class="wcg__pages" role="navigation" aria-label="Pagination">
      <button *ngFor="let p of pageButtons"
              class="wcg-page"
              [class.wcg-page--active]="p === page"
              [disabled]="p === '…'"
              (click)="onPageClick(p)">
        {{p}}
      </button>
    </div>

    <button class="wcg-btn wcg-btn--ghost" (click)="reload(page+1)" [disabled]="page>=pageCount">Next</button>
    <button class="wcg-btn wcg-btn--ghost" (click)="reload(pageCount)" [disabled]="page>=pageCount">Last</button>

    <span class="wcg__pager-spacer"></span>

    <label class="wcg__pager-label">Go:</label>
    <input class="wcg__input wcg__input--sm wcg__input--page"
           type="number"
           [min]="1"
           [max]="pageCount"
           [(ngModel)]="jumpTo"
           (keydown.enter)="jump()" />
    <button class="wcg-btn wcg-btn--ghost wcg-btn--xs" (click)="jump()">Go</button>

    <label class="wcg__pager-label">Page size:</label>
    <select class="wcg__select wcg__select--sm" [(ngModel)]="pageSize" (change)="onPageSizeChanged()">
      <option *ngFor="let n of [25,50,100,200]" [value]="n">{{n}}</option>
    </select>

    <span class="wcg__muted">{{total}} rows</span>
  </div>

  <wc-column-chooser
    *ngIf="showColumns"
    [columns]="userColumns"
    [visible]="visibleColumns"
    (visibleChange)="setVisibleColumns($event)"
    (close)="showColumns=false">
  </wc-column-chooser>

  <wc-record-editor
    *ngIf="editorOpen && def"
    [def]="def"
    [title]="editorTitle"
    [mode]="editorMode"
    [filter]="filter"
    [model]="editorModel"
    [context]="context"
    (save)="onEditorSave($event)"
    (cancel)="closeEditor()">
  </wc-record-editor>

</div>

  `
})
export class WcGridComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() gridKey?: string;
  private _dashboardId?: string;

  @Input()
  set dashboardId(v: string | undefined) {
    if (v === this._dashboardId) return;
    this._dashboardId = v;
    if (v) this.ngOnInit();
  }
  get dashboardId() { return this._dashboardId; }
  @Input() context: Record<string, any> = {};
  // New: choose whether the grid calls save endpoints or just edits in memory and emits ops externally
  @Input() editTransport: EditTransport = 'server';
  /** Set true only for grids that modify the current user's own permissions (e.g. user role grids). */
  @Input() reloadUserOnSave = false;

  /** Show a free-text quick-search box that filters rows server-side across text columns. */
  @Input() showQuickSearch = false;
  @Input() searchPlaceholder = 'Search...';

  quickSearch = '';
  private searchDebounce?: ReturnType<typeof setTimeout>;

  // New: emits whenever pending ops change (useful for a parent "Save All" button)
  @Output() pendingOpsChange = new EventEmitter<BulkOp[]>();

  def?: GridDefinition;
  editorOriginal: Record<string, any> = {};
  baseRows: Record<string, any>[] = [];
  rows: Record<string, any>[] = [];
  total = 0;

  page = 1;
  pageSize = 50;
  sort: SortDef[] = [];
  filter: FilterGroup = { logic: 'AND', rules: [], groups: [] };

  showColumns = false;
  visibleColumns: string[] = [];

  saving = false;
  dirtyCount = 0;

  editorOpen = false;
  editorMode: 'edit'|'create' = 'edit';
  editorTitle = 'Edit record';
  editorModel: Record<string, any> = {};
  editingRowId: string|null = null;

  selectedRowId: string | null = null;
  selectedRow: Record<string, any> | null = null;

  @Output() onEdit = new EventEmitter<Record<string, any> | null>();
  @Output() onCreate = new EventEmitter<void>();

  @Output() selectedRowChange = new EventEmitter<Record<string, any> | null>();
  @Input() contextMenuItems: GridContextMenuItem[] = [];
  @Output() contextMenuAction = new EventEmitter<{ id: string; row: Record<string, any> }>();

  // --- Column widths (field -> px) ---
  colWidths: Record<string, number> = {};

  @ViewChild('tableWrap', { static: false }) tableWrapRef?: ElementRef<HTMLElement>;

  private ro?: ResizeObserver;


  private get isDashboard(): boolean {
    return !!this.dashboardId;
  }
  private get instanceKey(): string {
    if (this.dashboardId) return `dashboard:${this.dashboardId}`;
    return this.gridKey ?? '';
  }

  private requireKeysOrExit(): boolean {
    if (this.dashboardId) return true;
    if (this.gridKey && this.gridKey.trim().length > 0) return true;

    console.error('wc-grid requires either [gridKey] or [dashboardId].');
    return false;
  }

  // computed widths used for rendering (base + distributed slack)
  private renderColWidths: Record<string, number> = {};

  // Use render widths in template

  private setBaseWidth(field: string, w: number) {
    this.colWidths[field] = w;
    this.reflowToFillWidth();
  }
  colWidth(field: string): number | null {
    return this.renderColWidths[field] ?? this.colWidths[field] ?? null;
  }

  private reflowToFillWidth() {
    if (!this.def) return;

    const wrap = this.tableWrapRef?.nativeElement;
    if (!wrap) {
      // can't compute container width yet; just mirror base widths
      this.renderColWidths = { ...this.colWidths };
      return;
    }

    const containerW = wrap.clientWidth;
    if (!containerW) {
      this.renderColWidths = { ...this.colWidths };
      return;
    }

    const cols = this.visibleColumnDefs;

    // include icon columns as fixed widths (tweak these if your CSS differs)
    const iconW =
      (this.def.canDelete ? 70 : 0) +
      (this.def.editMode === 'popup' ? 70 : 0);

    // base widths (mins)
    const base: Record<string, number> = {};
    let total = iconW;

    for (const c of cols) {
      const w = this.colWidths[c.field] ?? 160;
      base[c.field] = w;
      total += w;
    }

    // If already fills/exceeds container, render base widths as-is
    if (total >= containerW) {
      this.renderColWidths = base;
      return;
    }

    // Slack to distribute
    const slack = containerW - total;

    // Choose which columns can grow:
    // If your GridColumnDef already has `flex`, use it; otherwise default to true.
    // Columns the user (or a saved view) has already given an explicit width
    // are excluded so manual resizing sticks instead of being diluted by fill-slack.
    const flexCols = cols.filter(c => (c as any).flex !== false && this.colWidths[c.field] === undefined);

    if (flexCols.length === 0) {
      this.renderColWidths = base;
      return;
    }

    const addEach = Math.floor(slack / flexCols.length);
    let remainder = slack - addEach * flexCols.length;

    const out: Record<string, number> = { ...base };

    for (const c of flexCols) {
      const extra = addEach + (remainder > 0 ? 1 : 0);
      if (remainder > 0) remainder--;
      out[c.field] = base[c.field] + extra;
    }

    this.renderColWidths = out;
  }

  private resizingField: string | null = null;
  private resizeStartX = 0;
  private resizeStartW = 0;

  ngAfterViewInit(): void {
    // run once the view exists
    queueMicrotask(() => this.reflowToFillWidth());

    if (this.tableWrapRef?.nativeElement) {
      this.ro = new ResizeObserver(() => this.reflowToFillWidth());
      this.ro.observe(this.tableWrapRef.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.ro?.disconnect();
    clearTimeout(this.searchDebounce);
  }

  autoSizeColumn(evt: MouseEvent, field: string) {
    evt.preventDefault();
    evt.stopPropagation();

    const table = (evt.target as HTMLElement)?.closest('table');
    if (!table) return;

    // Find the column index for this field within visibleColumnDefs
    const colIndex = this.visibleColumnDefs.findIndex(c => c.field === field);
    if (colIndex < 0) return;

    // account for optional icon columns at the left
    const leftOffset =
      (this.def?.canDelete ? 1 : 0) +
      (this.def?.editMode === 'popup' ? 1 : 0);

    const thIndex = leftOffset + colIndex;

    // measure header text width
    const headerTh = table.querySelectorAll<HTMLTableCellElement>('thead th')[thIndex];
    const headerTextEl =
      headerTh?.querySelector<HTMLElement>('.wcg__th-text') ?? headerTh;

    let max = this.measureTextWidth(headerTextEl);

    // measure all rendered body cells in this column
    const bodyRows = table.querySelectorAll<HTMLTableRowElement>('tbody tr');
    for (const tr of Array.from(bodyRows)) {
      const td = tr.children.item(thIndex) as HTMLElement | null;
      if (!td) continue;

      // if your td has an inner span/div, prefer that; else td itself
      const contentEl =
        td.querySelector<HTMLElement>('.wcg__cell-text, .wcg__cell, span, div') ?? td;

      max = Math.max(max, this.measureTextWidth(contentEl));
    }

    // Add padding + sort glyph + resizer handle
    const padding = 24;
    const sortGlyph = 16;
    const handle = 10;

    let target = Math.ceil(max + padding + sortGlyph + handle);
    target = Math.max(80, Math.min(600, target));

    this.setBaseWidth(field, target);
    this.persistView();
  }

  private measureTextWidth(el?: HTMLElement | null): number {
    if (!el) return 0;

    // Prefer a Range measurement because it reflects actual DOM rendering.
    // Works well for typical text content.
    const range = document.createRange();

    // If element has child nodes, measure its contents; else measure itself.
    range.selectNodeContents(el);

    const rects = range.getClientRects();
    let width = 0;

    // Some elements return multiple rects (wrapping). Take the widest line.
    for (const r of Array.from(rects)) width = Math.max(width, r.width);

    // If no rects (e.g., empty), fallback to offsetWidth
    if (!width) width = el.scrollWidth || el.offsetWidth || 0;

    range.detach?.();
    return width;
  }

  startResize(ev: MouseEvent, field: string) {
    ev.preventDefault();
    ev.stopPropagation();

    this.resizingField = field;
    this.resizeStartX = ev.clientX;

    const th = (ev.target as HTMLElement).closest('th') as HTMLElement | null;
    if (th) th.classList.add('resizing');

    this.resizeStartW = this.colWidths[field] ?? (th?.getBoundingClientRect().width ?? 120);

    let dragged = false;

    const onMove = (e: MouseEvent) => {
      if (!this.resizingField) return;
      const dx = e.clientX - this.resizeStartX;
      if (Math.abs(dx) > 2) dragged = true;
      const next = Math.max(80, Math.round(this.resizeStartW + dx));
      this.setBaseWidth(this.resizingField, next);
    };

    const onUp = () => {
      if (th) th.classList.remove('resizing');
      window.removeEventListener('mousemove', onMove, true);
      window.removeEventListener('mouseup', onUp, true);
      this.resizingField = null;
      this.persistView();

      // If the mouse actually moved, swallow the click that the browser fires
      // after mouseup so it doesn't bubble up to the <th> and trigger toggleSort.
      if (dragged) {
        window.addEventListener('click', suppressClick, true);
      }
    };

    const suppressClick = (e: MouseEvent) => {
      e.stopPropagation();
      window.removeEventListener('click', suppressClick, true);
    };

    window.addEventListener('mousemove', onMove, true);
    window.addEventListener('mouseup', onUp, true);
  }



  get pageCount() {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get userColumns(): GridColumnDef[] {
    return (this.def?.columns ?? []).filter(c => !c.field.startsWith('__'));
  }

  get visibleColumnDefs(): GridColumnDef[] {
    if (!this.def) return [];
    const all = this.def.columns.filter(c => (!c.field.startsWith('__') || c.field.startsWith('__Client')) && !c.hideInGrid);
    if (this.visibleColumns.length === 0) return all;
    const set = new Set(this.visibleColumns);
    return all.filter(c => set.has(c.field));
  }

  get filterableColumns(): GridColumnDef[] {
    return (this.def?.columns ?? []).filter(c => c.filterable && (!c.field.startsWith('__') || c.field.startsWith('__Client')));
  }

  /** Text columns the quick-search box spans. Prefers filterable text columns; falls back to all text columns. */
  get searchableColumns(): GridColumnDef[] {
    const isText = (c: GridColumnDef) =>
      c.dataType === 'String' && !c.hideInGrid &&
      (!c.field.startsWith('__') || c.field.startsWith('__Client'));
    const cols = this.def?.columns ?? [];
    const filterable = cols.filter(c => c.filterable && isText(c));
    return filterable.length ? filterable : cols.filter(isText);
  }

  constructor(private api: GridApiService, private edits: GridEditStore, private loginService: LoginService) {}

  ngOnInit(): void {

    if (!this.requireKeysOrExit()) return;
    if (this.isDashboard) {
      this.editTransport = 'memory';
    }
    const onDef = (def: GridDefinition) => {
      if (this.isDashboard) {
        def.gridEditable = false;
        def.canCreate = false;
        def.canDelete = false;
      }

      this.def = def;

      const defaults = {
        visibleColumns: def.columns.filter(c => !c.field.startsWith('__') || c.field.startsWith('__Client')).map(c => c.field),
        pageSize: 50,
        sort: [] as SortDef[],
        filter: { logic: 'AND', rules: [], groups: [] } as FilterGroup,
        colWidths: this.colWidths
      };

      const view = loadView(this.instanceKey, defaults);
      this.visibleColumns = view.visibleColumns;
      this.pageSize = view.pageSize;
      this.sort = view.sort;
      this.filter = view.filter;
      this.colWidths = view.colWidths ?? {};
      this.reflowToFillWidth();

      this.reload(1);
      this.recountDirty();
    };

    if (this.isDashboard) {
      this.api.dashboardDefinition(this.dashboardId!, onDef);
    } else {
      this.api.definition(this.gridKey!, onDef);
    }
  }

  private persistView() {
    if (!this.def) return;
    saveView(this.instanceKey, {
      visibleColumns: this.visibleColumns,
      pageSize: this.pageSize,
      sort: this.sort,
      filter: this.filter,
      colWidths: this.colWidths
    });
  }

  resetView() {
    clearView(this.instanceKey);
    this.visibleColumns = [];
    this.sort = [];
    this.filter = { logic: 'AND', rules: [], groups: [] };
    this.pageSize = 50;
    this.colWidths = {};
    this.persistView();
    this.reload(1);
  }

  toggleColumns() { this.showColumns = !this.showColumns; }

  setVisibleColumns(cols: string[]) {
    this.visibleColumns = cols;
    this.persistView();
    this.reflowToFillWidth();
  }

  onFilterChanged() {
    this.persistView();
    this.reload(1);
  }

  get displayRows(): Record<string, any>[] {
    const term = this.quickSearch.trim().toLowerCase();
    if (!term) return this.rows;
    const fields = this.searchableColumns.map(c => c.field);
    if (!fields.length) return this.rows;
    return this.rows.filter(row =>
      fields.some(f => {
        const v = row[f];
        return v != null && String(v).toLowerCase().includes(term);
      })
    );
  }

  onQuickSearchInput(value: string) {
    this.quickSearch = value ?? '';

    // The grid only ever holds one page of rows client-side, so a match outside
    // that page (e.g. "operation" for a table 200 rows down an alphabetical list)
    // would never surface without re-querying the server. Debounce so we don't
    // fire a request per keystroke.
    clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => this.reload(1), 300);
  }

  clearQuickSearch() {
    this.quickSearch = '';
    clearTimeout(this.searchDebounce);
    this.reload(1);
  }

  /** OR-group of Contains rules across text columns, or null when the box is empty. */
  private buildQuickSearchGroup(): FilterGroup | null {
    const term = this.quickSearch.trim();
    if (!term) return null;
    const cols = this.searchableColumns;
    if (!cols.length) return null;
    return {
      logic: 'OR',
      rules: cols.map(c => ({ field: c.field, op: 'Contains' as const, value: term })),
      groups: []
    };
  }

  /** Combine the structured filter with the quick-search term (AND) for the data query. */
  private effectiveFilter(): FilterGroup {
    const qs = this.buildQuickSearchGroup();
    if (!qs) return this.filter;

    const hasBaseFilter = this.filter.rules.length > 0 || (this.filter.groups?.length ?? 0) > 0;
    if (!hasBaseFilter) return qs;

    return { logic: 'AND', rules: [], groups: [this.filter, qs] };
  }

  onPageSizeChanged() {
    this.persistView();
    this.reload(1);
  }

  reload(page: number) {
    if (!this.def) return;
    this.page = Math.max(1, page);

    const q = {
      page: this.page,
      pageSize: this.pageSize,
      sort: this.sort,
      filter: this.effectiveFilter(),
      context: this.context
    };

    const onPage = (res: GridPage) => {
      this.baseRows = structuredClone(res.records);
      this.rows = this.edits.apply(this.instanceKey, this.def!.idField, res.records);
      this.total = res.totalCount;
      this.recountDirty();
      queueMicrotask(() => this.reflowToFillWidth());
    };

    if (this.isDashboard) {
      this.api.dashboardData(this.dashboardId!, q, onPage);
    } else {
      this.api.data(this.gridKey!, q, onPage);
    }
  }

  toggleSort(c: GridColumnDef) {
    if (!c.sortable) return;
    const idx = this.sort.findIndex(s => s.field === c.field);
    if (idx < 0) this.sort = [{ field: c.field, dir: 'asc' }];
    else if (this.sort[idx].dir === 'asc') this.sort = [{ field: c.field, dir: 'desc' }];
    else this.sort = [];
    this.persistView();
    this.reload(1);
  }

  sortDir(field: string) {
    return this.sort.find(s => s.field === field)?.dir;
  }

  isReadOnlyCell(r: any, c: any): boolean {
    // grid-level + row-level + column-level rules; adapt to your actual helpers
    return !this.isColumnEditable(c, r);
  }

  // Row-level permissions: backend can return __canEdit/__canDelete
  canRowEdit(row: Record<string, any>): boolean {
    if (!this.def) return false;
    if (!this.def.gridEditable) return false;
    const v = row['__canEdit'];
    return v === undefined ? true : !!v;
  }

  canRowDelete(row: Record<string, any>): boolean {
    if (!this.def) return false;
    if (!this.def.canDelete) return false;
    const v = row['__canDelete'];
    return v === undefined ? true : !!v;
  }

  // Per-field row permissions: backend can return __roFields as "field1,field2"
  private rowReadOnlySet(row: Record<string, any>): Set<string> {
    const raw = row['__roFields'];
    if (!raw) return new Set();
    const parts = String(raw).split(',').map(s => s.trim()).filter(Boolean);
    return new Set(parts);
  }

  isColumnEditable(c: GridColumnDef, row: Record<string, any>): boolean {
    if (!this.def) return false;
    if (!c.editable) return false;
    if (c.readOnlyReason) return false;
    if (!this.canRowEdit(row)) return false;

    const ro = this.rowReadOnlySet(row);
    if (ro.has(c.field)) return false;

    return true;
  }

  cellMode(c: GridColumnDef, row: Record<string, any>): string {
    if (!this.def) return 'display';
    const rowId = String(row[this.def.idField]);
    if (this.edits.isRowDeleted(this.instanceKey, rowId) || row['__rowDeleted']) return 'display';

    if (this.def.editMode === 'popup') return 'display';

    if (!this.isColumnEditable(c, row)) return 'display';

    // Booleans always render as checkboxes (clicking a checkbox is self-contained)
    if (c.dataType === 'Bool') return 'edit-bool';

    // All other editable cells only show their input when focused
    if (!this.isCellFocused(row, c)) return 'display';

    if (c.lookupKey) return 'edit-lookup';

    switch (c.dataType) {
      case 'Int':
      case 'Double': return 'edit-number';
      case 'Date': return 'edit-date';
      case 'DateTime': return 'edit-datetime';
      default: return 'edit-text';
    }
  }

  focusedCell: { rowId: string; field: string } | null = null;

  isCellFocused(row: Record<string, any>, c: GridColumnDef): boolean {
    if (!this.def || !this.focusedCell) return false;
    const rowId = String(row[this.def.idField]);
    return this.focusedCell.rowId === rowId && this.focusedCell.field === c.field;
  }

  focusCell(row: Record<string, any>, c: GridColumnDef) {
    if (!this.def) return;
    if (!this.isColumnEditable(c, row)) return;
    if (this.isRowDeleted(row)) return;
    if (c.dataType === 'Bool') return;
    const rowId = String(row[this.def.idField]);
    this.focusedCell = { rowId, field: c.field };
    queueMicrotask(() => {
      const input = this.tableWrapRef?.nativeElement?.querySelector<HTMLElement>(
        '.wcg__td--focused input, .wcg__td--focused select, .wcg__td--focused .wcg__control input'
      );
      input?.focus();
    });
  }

  onCellFocusOut(event: FocusEvent) {
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    const currentTarget = event.currentTarget as HTMLElement;
    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      this.focusedCell = null;
    }
  }

  onInlineEdit(row: Record<string, any>, field: string, value: any) {
    if (!this.def) return;
    const rowId = String(row[this.def.idField]);
    this.edits.upsert(this.instanceKey, rowId, field, value);
    this.recountDirty();
  }

  isDirty(row: Record<string, any>, field: string) {
    if (!this.def) return false;
    const rowId = String(row[this.def.idField]);
    return this.edits.isCellDirty(this.instanceKey, rowId, field);
  }

  isRowDeleted(row: Record<string, any>) {
    if (!this.def) return false;
    const rowId = String(row[this.def.idField]);
    return this.edits.isRowDeleted(this.instanceKey, rowId) || !!row['__rowDeleted'];
  }

  deleteOpen = false;
  deleting = false;
  deleteRowId: string | null = null;
  deleteRowLabel: string | null = null;

  requestDelete(row: Record<string, any>) {
    if (!this.def) return;
    if (!this.canRowDelete(row)) return;

    // Inline mode: keep your existing staged delete behavior
    if (this.def.editMode !== 'popup') {
      this.markDelete(row);
      return;
    }

    // Popup mode: confirm + delete immediately
    this.deleteRowId = String(row[this.def.idField]);

    // Optional: pick a nicer label if you have a Name/Description field
    this.deleteRowLabel = row['Name'] ?? row['Description'] ?? this.deleteRowId;

    this.deleteOpen = true;
  }

  closeDelete() {
    this.deleteOpen = false;
    this.deleteRowId = null;
    this.deleteRowLabel = null;
    this.deleting = false;
  }

  confirmDelete() {
    if (!this.def) return;
    if (!this.deleteRowId) return;

    this.deleting = true;

    const ops: BulkOp[] = [];
    ops.push({
        rowId: this.deleteRowId!,
        op: "Delete"
    });

    this.api.bulkOps(this.instanceKey, { ops, context: this.context }, () => {

      this.deleting = false;
      this.closeDelete();
      this.reload(this.page);
    }, () => {
      this.deleting = false;
    });
  }

  markDelete(row: Record<string, any>) {
    if (!this.def) return;
    const rowId = String(row[this.def.idField]);
    this.edits.markDelete(this.instanceKey, rowId);
    row['__rowDeleted'] = true;
    this.recountDirty();
  }

  undoDelete(row: Record<string, any>) {
    if (!this.def) return;
    const rowId = String(row[this.def.idField]);
    this.edits.undoDelete(this.instanceKey, rowId);
    delete row['__rowDeleted'];
    this.recountDirty();
  }

  recountDirty() {
    this.dirtyCount = this.edits.dirtyCount(this.instanceKey);
    this.pendingOpsChange.emit(this.edits.getOps(this.instanceKey, this.def?.idField));
  }

  // Soft-check confirm state (see def.preCheck): the warnings and what to do on each answer.
  warnOpen = false;
  warnings: string[] = [];
  private warnProceed: (() => void) | null = null;
  private warnDecline: (() => void) | null = null;

  proceedPastWarnings() {
    const go = this.warnProceed;
    this.closeWarnings();
    go?.();
  }

  declineWarnings() {
    const back = this.warnDecline;
    this.closeWarnings();
    back?.();
  }

  private closeWarnings() {
    this.warnOpen = false;
    this.warnings = [];
    this.warnProceed = null;
    this.warnDecline = null;
  }

  /**
   * @param onError    called when the save fails
   * @param onDeclined called when the user backs out of a soft-check warning; falls back to onError
   */
  saveToServer(onError?: () => void, onDeclined?: () => void) {
    if (!this.def) return;

    this.saving = true;
    const ops = this.edits.getOps(this.instanceKey, this.def.idField);

    if (!this.validateBeforeSave(ops)) {
      // stop save, show errors in grid
      this.saving = false;

      // optional: jump to first bad row/page if you want
      // optional: toast/snackbar
      return;
    }

    const send = () => this.api.bulkOps(this.instanceKey, { ops, context: this.context }, async() => {
      if (this.reloadUserOnSave) await this.loginService.reload();
      this.validationErrors.clear();
      this.edits.clear(this.instanceKey);
      this.saving = false;
      this.reload(this.page);
    }, () => {
      this.saving = false;
      onError?.();
    });

    // Grids that opt in get a chance to warn about new records before they are saved.
    const creates = ops.filter(o => o.op === 'Create');
    if (!this.def.preCheck || creates.length === 0) {
      send();
      return;
    }

    this.api.preCheck(this.instanceKey, { ops: creates, context: this.context }, r => {
      if (!r.warnings?.length) {
        send();
        return;
      }
      this.warnings = r.warnings;
      this.warnProceed = send;
      this.warnDecline = () => {
        this.saving = false;
        (onDeclined ?? onError)?.();
      };
      this.warnOpen = true;
    });
  }


  private validateBeforeSave(ops: BulkOp[]): boolean {
    if (!this.def) return true;

    this.validationErrors.clear();

    const requiredCols = this.def.columns
      .filter(c => !c.field.startsWith('__'))
      .filter(c => c.required);

    // map visible/current rows by id so we can validate "final" row values
    const idField = this.def.idField;
    const rowById = new Map<string, Record<string, any>>(
      this.rows.map(r => [String(r[idField]), r])
    );

    for (const op of ops) {
      // deletes don't need required validation
      if (op.op === 'Delete') continue;

      // For Create, the row isn't in this.rows yet (no reload ran), so read from the patch.
      // For Update, the row in this.rows already has the patch applied via the UI.
      const merged = op.op === 'Create' ? (op.patch ?? {}) : (rowById.get(op.rowId) ?? {});

      const errs: Record<string, string> = {};

      for (const c of requiredCols) {
        // If this field is not editable for this row, you can skip or still require.
        // Usually skip if it's read-only (server should enforce anyway).
        // if (row && !this.isColumnEditable(c, row)) continue;

        const v = merged[c.field];

        const missing =
          v === null ||
          v === undefined ||
          (typeof v === 'string' && v.trim() === '');

        if (missing) errs[c.field] = 'Required';
      }

      if (Object.keys(errs).length) {
        this.validationErrors.set(op.rowId, errs);
      }
    }

    return this.validationErrors.size === 0;
  }

  discard() {
    if (!this.def) return;

    this.edits.clear(this.instanceKey);
    const idField = this.def.idField;
    const baseById = new Map(this.baseRows.map(r => [String(r[idField]), r]));

    for (const r of this.rows) {
      const id = String(r[idField]);
      const base = baseById.get(id);
      if (!base) continue;

      for (const k of Object.keys(r)) delete (r as any)[k];
      Object.assign(r, structuredClone(base));
    }
    for (const r of this.rows) delete r['__rowDeleted'];

    this.recountDirty();
    this.rows = [...this.rows];
  }


  openEdit(row: Record<string, any>) {
    if (!this.def) return;
    if (!this.canRowEdit(row)) return;

    if (this.onEdit.observed) {
      this.onEdit.emit(row);
      this.reload(1);
      return;
    }

    this.editorMode = 'edit';
    this.editorTitle = `Edit ${this.def.title}`;
    this.editorOpen = true;

    const ro = this.rowReadOnlySet(row);

    const m: Record<string, any> = {};
    const o: Record<string, any> = {};

    for (const c of this.def.columns) {
      if (c.field.startsWith('__') && !c.field.startsWith('__Client')) continue;
      if (!c.editable) continue;
      if (c.readOnlyReason) continue;
      if (ro.has(c.field)) continue;

      const cur = row[c.field];

      if (c.dataType === 'Date') {
        m[c.field] = this.toIsoDateOnly(cur); 
        o[c.field] = this.toIsoDateOnly(cur);
      } else if (c.dataType === 'DateTime') {
        m[c.field] = this.toLocalDateTime(cur);
        o[c.field] = this.toLocalDateTime(cur);
      } else {
        m[c.field] = cur;
        o[c.field] = cur;
      }
    }

    m[this.def.idField] = row[this.def.idField];
    o[this.def.idField] = row[this.def.idField];

    this.editorModel = m;
    this.editorOriginal = o;
    this.editingRowId = String(row[this.def.idField]);
  }

  openCreate() {
    if (this.onCreate.observed) {
      this.onCreate.emit();
      this.reload(1);
      return;
    }

    if (!this.def) return;

    if (this.def.editMode === 'inline') {
      const tempId = this.edits.newTempId();

      const row: Record<string, any> = {};
      row[this.def.idField] = tempId; // ✅ critical

      for (const c of this.def.columns) {
        if (c.field.startsWith('__') && !c.field.startsWith('__Client')) continue;
        if (c.field === this.def.idField) continue;

        row[c.field] = this.columnDefault(c);
      }

      this.edits.stageCreate(this.instanceKey, tempId, row, this.def.idField);

      this.rows = [row, ...this.rows];
      this.total++;
      this.recountDirty();
      return;
    }

    this.editorMode = 'create';
    this.editorTitle = `Add ${this.def.title}`;
    this.editorOpen = true;

    const m: Record<string, any> = {};
    for (const c of this.def.columns) {
      if (c.field.startsWith('__') && !c.field.startsWith('__Client')) continue;
      if (c.field === this.def.idField || c.required || c.editable) {
        m[c.field] = this.columnDefault(c);
      }
    }
    this.editorModel = m;
    this.editingRowId = null;
  }

  private columnDefault(c: GridColumnDef): any {
    if (c.defaultValue !== undefined && c.defaultValue !== null) return c.defaultValue;
    return c.dataType === 'Bool' ? false : null;
  }

  closeEditor() {
    this.editorOpen = false;
    this.editorModel = {};
    this.editingRowId = null;
  }

  onEditorSave(model: Record<string, any>) {
    if (!this.def) return;

    if (this.editorMode === 'create') {
      const tempId = this.edits.newTempId();
      this.edits.stageCreate(this.instanceKey, tempId, model, this.def.idField);
      this.closeEditor();
      // A rejected create (e.g. server-side validation) must not stay staged --
      // otherwise it keeps resending on every later save and, since bulk ops are
      // applied in order and abort on the first failure, permanently blocks any
      // new row from ever being attempted.
      this.saveToServer(
        () => this.edits.remove(this.instanceKey, tempId),
        // Backing out of a soft-check warning returns to the form with what was typed, so a typo can be fixed.
        () => {
          this.edits.remove(this.instanceKey, tempId);
          this.editorMode = 'create';
          this.editorTitle = `Add ${this.def!.title}`;
          this.editorModel = model;
          this.editingRowId = null;
          this.editorOpen = true;
        });
      return;
    }

    const rowId = this.editingRowId!;
    const patchForUi: Record<string, any> = {};

    for (const [k, v] of Object.entries(model)) {
      if (k === this.def.idField) continue;

      const oldV = this.editorOriginal[k];
      if (this.valuesEqual(oldV, v) && !this.def.sendAllColumns) continue;

      const col = this.def.columns.find(c => c.field === k);

      if (col?.dataType === 'Date') {
        const iso = this.fromIsoDateOnly(v as any);
        this.edits.upsert(this.instanceKey, rowId, k, iso);
        patchForUi[k] = iso;
      } else if (col?.dataType === 'DateTime') {
        const iso = this.fromLocalDateTime(v as any);
        this.edits.upsert(this.instanceKey, rowId, k, iso);
        patchForUi[k] = iso;
      } else {
        this.edits.upsert(this.instanceKey, rowId, k, v);
        patchForUi[k] = v;
      }
    }
    this.closeEditor();
    this.saveToServer();
  }

  private valuesEqual(a: any, b: any) {
    const na = (a === undefined ? null : a);
    const nb = (b === undefined ? null : b);
    return na === nb;
  }


  formatCell(val: any, type: any, fmt?: string) {
    if (val == null) return '';
    switch (type) {
      case 'Int':
      case 'Double': return this.formatNumber(Number(val), fmt);
      case 'Date': 
      case 'DateTime':
        return this.formatDateTime(val, fmt);
      case 'Bool': return val ? '✓' : '';
      default: return String(val);
    }
  }

  formatNumber(val: number, fmt?: string): string {
    if (!fmt) return String(val);

    // Simple .NET-style subset
    if (/^C\d+$/.test(fmt)) {
      const digits = parseInt(fmt.substring(1), 10);
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',   // or from user settings
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
      }).format(val);
    }

    if (/^N\d+$/.test(fmt)) {
      const digits = parseInt(fmt.substring(1), 10);
      return new Intl.NumberFormat(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
      }).format(val);
    }

    if (/^D\d+$/.test(fmt)) {
      return Math.trunc(val).toString().padStart(parseInt(fmt.substring(1), 10), '0');
    }

    return String(val);
  }

  formatDateTime(val: any, fmt?: string): string {
    if (!val) return '';

    const s = String(val).replace(' ', 'T');
    const [datePart, timePart = ''] = s.split('T');
    const [yyyy, MM, dd] = datePart.split('-');
    const [HH = '00', mm = '00', ss = '00'] = timePart.split(':');

    if (!fmt) return `${yyyy}-${MM}-${dd} ${HH}:${mm}:${ss}`;

    return fmt
      .replace('yyyy', yyyy)
      .replace('MM', MM)
      .replace('dd', dd)
      .replace('HH', HH)
      .replace('mm', mm)
      .replace('ss', ss);
  }

  // ----- DATE (naive) -----
  toIsoDateOnly(val: any): string | null {
    if (!val) return null;
    const s = String(val);
    // Accept "2026-02-13", or "2026-02-13T12:34:56"
    return s.length >= 10 ? s.substring(0, 10) : null;
  }

  fromIsoDateOnly(val: string | null): string | null {
    // Keep as naive date-only string
    return val && val.trim() ? val : null;
  }

  // ----- DATETIME (naive, no timezone) -----
  toLocalDateTime(val: any): string | null {
    if (!val) return null;
    const s = String(val).trim();

    // If server sends "YYYY-MM-DD HH:mm:ss", normalize:
    const normalized = s.includes(' ') ? s.replace(' ', 'T') : s;

    // Input datetime-local wants "YYYY-MM-DDTHH:mm" (seconds optional)
    if (normalized.length >= 16) return normalized.substring(0, 16); // drop seconds for the control
    return normalized;
  }

  fromLocalDateTime(val: string | null): string | null {
    if (!val || !val.trim()) return null;

    // datetime-local gives "YYYY-MM-DDTHH:mm" (no tz). Add seconds so server gets stable format:
    return val.length === 16 ? `${val}:00` : val;
  }

  jumpTo = 1;

  // what the template loops over:
  get pageButtons(): Array<number | '…'> {
    return this.buildPageButtons(this.page, this.pageCount);
  }

  private buildPageButtons(cur: number, total: number): Array<number | '…'> {
    if (total <= 1) return [1];

    // Config knobs
    const edgeCount = 1;     // how many pages always shown at start/end
    const aroundCount = 2;   // how many pages shown on each side of current

    const pages = new Set<number>();

    // edges
    for (let i = 1; i <= edgeCount; i++) pages.add(i);
    for (let i = total - edgeCount + 1; i <= total; i++) pages.add(i);

    // around current
    for (let i = cur - aroundCount; i <= cur + aroundCount; i++) {
      if (i >= 1 && i <= total) pages.add(i);
    }

    const sorted = Array.from(pages).sort((a, b) => a - b);

    // add ellipses where gaps exist
    const out: Array<number | '…'> = [];
    for (let i = 0; i < sorted.length; i++) {
      const p = sorted[i];
      if (i > 0) {
        const prev = sorted[i - 1];
        if (p - prev === 2) out.push(prev + 1);      // fill single gap
        else if (p - prev > 2) out.push('…');        // big gap
      }
      out.push(p);
    }
    return out;
  }

  jump() {
    const p = Math.max(1, Math.min(this.pageCount, Number(this.jumpTo || 1)));
    this.reload(p);
  }

  onPageClick(p: number | '…') {
    if (p === '…') return;
    this.reload(p);
  }

  isSelected(r: Record<string, any>) {
    if (!this.def) return false;
    return this.selectedRowId === String(r[this.def.idField]);
  }

  selectRow(r: Record<string, any>) {
    if (!this.def) return;
    this.selectedRowId = String(r[this.def.idField]);
    this.selectedRow = r;
    this.selectedRowChange.emit(r);
  }

  onRowContextMenu(ev: MouseEvent, r: Record<string, any>) {
    ev.preventDefault();
    ev.stopPropagation();
    this.selectRow(r);           // right click also selects
    this.openContextMenu(ev, r); // next section
  }

  menuOpen = false;
  menuX = 0;
  menuY = 0;
  menuRow: Record<string, any> | null = null;

  private openContextMenu(ev: MouseEvent, row: Record<string, any>) {
    if (!this.contextMenuItems?.length) return;

    // if already open, reset first (prevents "recycle" weirdness)
    this.menuOpen = false;

    this.menuRow = row;
    this.menuX = ev.clientX;
    this.menuY = ev.clientY;
    this.menuOpen = true;

    // Close on outside click + escape
    queueMicrotask(() => {
      const onClick = (e: MouseEvent) => {
        // if click is inside the menu, ignore
        const el = e.target as HTMLElement;
        if (el.closest('.wcg-menu')) return;
        this.closeMenu();
        window.removeEventListener('click', onClick, true);
        window.removeEventListener('keydown', onKey, true);
      };

      const onKey = (e: KeyboardEvent) => {
        if (e.key !== 'Escape') return;
        this.closeMenu();
        window.removeEventListener('click', onClick, true);
        window.removeEventListener('keydown', onKey, true);
      };

      window.addEventListener('click', onClick, true);
      window.addEventListener('keydown', onKey, true);
    });
  }


  closeMenu() {
    this.menuOpen = false;
    this.menuRow = null;
  }

  runMenu(id: string) {
    if (!this.menuRow) return;
    this.contextMenuAction.emit({ id, row: this.menuRow });
    this.closeMenu();
  }

  // rowId -> field -> error message
  validationErrors = new Map<string, Record<string, string>>();

  hasValidationErrors(): boolean {
    return this.validationErrors.size > 0;
  }

  getCellError(row: Record<string, any>, field: string): string | null {
    if (!this.def) return null;
    const rowId = String(row[this.def.idField]);
    return this.validationErrors.get(rowId)?.[field] ?? null;
  }

  isCellInvalid(row: Record<string, any>, field: string): boolean {
    return !!this.getCellError(row, field);
  }
}
export interface GridContextMenuItem {
  label: string;
  id: string;
  disabled?: (row: Record<string, any>) => boolean;
}


export interface GridColumn {
  field: string;
  header: string;
  widthPx?: number;
  minWidthPx?: number;
  flex?: boolean;   // if true, can absorb leftover width
}
